import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, LogIn, GraduationCap } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { parseApiError } from '../../lib/apiErrors';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_e21507e1-a0e4-40f2-a327-b19a660901a9/artifacts/9i39bysf_475444528_1384443796015613_5677894820733752221_n%20%281%29.jpg';

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pendingApproval = searchParams.get('pending') === '1';
  const { login } = useAuth();
  const { toast } = useToast();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast({ title: 'Connexion réussie', description: `Bienvenue ${user.prenom} !` });
      if (user.role === 'admin' || user.role === 'super_admin' || user.role === 'agence') {
        navigate('/admin');
      } else if (user.role === 'eleve' && user.is_approved) {
        navigate('/espace-eleve');
      } else {
        navigate('/espace-apprenant');
      }
    } catch (error) {
      toast({
        title: 'Erreur de connexion',
        description: parseApiError(error, 'Email ou mot de passe incorrect'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#285BBB] to-[#2C3C65] flex items-center justify-center p-4" data-testid="login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <img
              src={LOGO_URL}
              alt="The English Speaking Center"
              className="h-16 mx-auto mb-4"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="hidden items-center justify-center text-[#285BBB] font-bold text-2xl mb-4">
              <GraduationCap className="h-10 w-10 mr-2" />
              <span>The English Speaking Center</span>
            </div>
            <h1 className="text-2xl font-bold text-[#2C3C65]">Connexion</h1>
            <p className="text-[#6777A1] mt-2">Accédez à votre espace</p>
          </div>

          {pendingApproval && (
            <div
              className="mb-6 p-4 rounded-xl border-2 border-amber-300 bg-amber-50"
              data-testid="login-pending-banner"
            >
              <h3 className="font-semibold text-amber-900 mb-1">⏳ Compte en attente de validation</h3>
              <p className="text-sm text-amber-800">
                Votre demande de création de compte a bien été enregistrée. Un administrateur la validera prochainement.
                Vous recevrez un email dès l'activation de votre compte.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                required
                className="w-full"
                data-testid="login-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">Mot de passe</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full pr-10"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-gray-400 hover:text-[#285BBB] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#285BBB] z-10 transition-colors"
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  tabIndex={0}
                  data-testid="login-password-toggle"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#285BBB] hover:bg-[#2C3C65] text-white py-3"
              data-testid="login-submit"
            >
              {loading ? 'Connexion...' : (
                <>
                  <LogIn className="h-5 w-5 mr-2" />
                  Se connecter
                </>
              )}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <Link
              to="/forgot-password"
              className="text-sm text-[#285BBB] hover:underline"
              data-testid="forgot-password-link"
            >
              Mot de passe oublié ?
            </Link>
          </div>

          <div className="mt-6 text-center">
            <p className="text-[#6777A1]">
              Pas encore de compte ?{' '}
              <Link to="/register" className="text-[#285BBB] font-medium hover:underline">
                Créer un compte
              </Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link to="/" className="text-sm text-[#6777A1] hover:text-[#285BBB]">
              ← Retour à l'accueil
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
